import { useMemo } from 'react';

import { useSearchParams } from 'react-router-dom';

import {
  parseDateRange,
  serializeDateRange,
} from '@/components/data-table/filters-v1/components/data-table-date-range-picker-v2-filter';
import {
  DateRangePreset,
  DateRangeV2,
  detectPresetFromDates,
  getDateRangeFromPreset,
} from '@/modules/common/components/molecules/date/date-range-picker-v2';

import { FILTER_KEY_DATE_RANGE } from '../constants/time-offs-filter-keys';

interface UseTimeOffsDateRangeParamResult {
  dates: DateRangeV2 | null;
  selectedPreset: DateRangePreset | null;
  /**
   * Raw serialized param value (or null). The date-range lives outside the
   * filters-v2 config, so `useTableFilters` strips it from `activeFilters`;
   * the page merges this back into the filters it feeds the table query.
   */
  serialized: string | null;
  onChange: (next: DateRangeV2) => void;
  onPresetSelect: (preset: DateRangePreset) => void;
  onReset: () => void;
}

/**
 * Reads/writes the time-offs date-range as a URL search param, so the selected
 * range survives reloads and is shareable. Keeps the page component free of
 * URLSearchParams plumbing.
 */
export const useTimeOffsDateRangeParam = (): UseTimeOffsDateRangeParamResult => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { dates, selectedPreset, serialized } = useMemo(() => {
    const raw = searchParams.get(FILTER_KEY_DATE_RANGE);
    const parsed = raw ? parseDateRange(raw) : null;
    return {
      dates: parsed,
      selectedPreset: parsed ? detectPresetFromDates(parsed) : null,
      serialized: parsed ? raw : null,
    };
  }, [searchParams]);

  const writeParam = (value: string | null) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev.toString());
        if (value) {
          next.set(FILTER_KEY_DATE_RANGE, value);
        } else {
          next.delete(FILTER_KEY_DATE_RANGE);
        }
        return next;
      },
      { replace: true }
    );
  };

  return {
    dates,
    selectedPreset,
    serialized,
    onChange: next => writeParam(serializeDateRange(next)),
    onPresetSelect: preset => writeParam(serializeDateRange(getDateRangeFromPreset(preset))),
    onReset: () => writeParam(null),
  };
};
